import {useCallback, useEffect, useState} from 'react';

import {type LogLineData} from '../LogLineRenderer';

const escapeRegexChars = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface LogSearchMatches {
  ordered: {row: number; col: number}[];
  indexed: Record<number, number[]>;
}

export interface LogSearchReturn {
  searchText: string;
  searchActive: number;
  searchMatches: LogSearchMatches;
  onSearchChange: (value: string) => void;
  onSetIndex: (index: number) => void;
  onKeyDown: (key: string) => void;
  goToNext: () => void;
  goToPrev: () => void;
}

/**
 * useLogSearch manages search functionality for log lines, including match
 * tracking and navigation. Provides case-insensitive search with support for
 * using the 'enter' key to navigate through results.
 */
export function useLogSearch(
  filteredLines: readonly LogLineData[],
  scrollTo: (row: number) => void
): LogSearchReturn {
  // Current match focused by the user.
  const [searchActive, setSearchActive] = useState(-1);
  const [searchMatches, setSearchMatches] = useState<LogSearchMatches>({
    ordered: [],
    indexed: {},
  });

  const [searchText, setSearchText] = useState('');

  const onSearchChange = (value: string) => {
    const matches: LogSearchMatches = {
      ordered: [] as {row: number; col: number}[],
      indexed: {} as Record<number, number[]>,
    };
    if (value) {
      const regex = new RegExp(escapeRegexChars(value), 'ig');
      for (let i = 0; i < filteredLines.length; i++) {
        const text = filteredLines[i].line;
        for (const match of text.matchAll(regex)) {
          const {index} = match;
          if (index != null) {
            matches.ordered.push({row: i, col: index});
            if (i in matches.indexed) {
              matches.indexed[i].push(index);
            } else {
              matches.indexed[i] = [index];
            }
          }
        }
      }
    }

    setSearchText(value);
    setSearchMatches(matches);
    setSearchActive(matches.ordered.length ? 0 : -1);

    if (matches.ordered.length) {
      scrollTo(matches.ordered[0].row);
    }
  };

  // When displayed lines change, we need to refresh search states.
  useEffect(() => {
    if (searchText) {
      onSearchChange(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLines]);

  const onSetIndex = useCallback(
    (index: number) => {
      setSearchActive(index);
      scrollTo(searchMatches.ordered[index].row);
    },
    [scrollTo, searchMatches.ordered]
  );

  const goToNext = useCallback(() => {
    if (searchMatches.ordered.length === 0) {
      return;
    }

    const nextIndex = (searchActive + 1) % searchMatches.ordered.length;
    onSetIndex(nextIndex);
  }, [searchActive, searchMatches.ordered.length, onSetIndex]);

  const goToPrev = useCallback(() => {
    if (searchMatches.ordered.length === 0) {
      return;
    }

    const prevIndex =
      (searchActive - 1 + searchMatches.ordered.length) %
      searchMatches.ordered.length;
    onSetIndex(prevIndex);
  }, [searchActive, searchMatches.ordered.length, onSetIndex]);

  const onKeyDown = useCallback(
    (key: string) => {
      if (key === 'Enter') {
        goToNext();
      }
    },
    [goToNext]
  );

  return {
    searchText,
    searchActive,
    searchMatches,
    onSearchChange,
    onSetIndex,
    onKeyDown,
    goToNext,
    goToPrev,
  };
}
